/**
 * WMS UI kit — core pieces (docs/DESIGN.md). Sentence case, 44 px minimum targets
 * (48 px with size="lg" for primary phone actions), visible focus, tokens only.
 */
import { AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { ApiError, legalHolds } from '../../lib/api';

export const cx = (...c: (string | false | null | undefined)[]): string => twMerge(c.filter(Boolean).join(' '));

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet';
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-ink hover:bg-brand/90 active:bg-brand/80 shadow-[inset_0_-1px_0_rgb(0_0_0/0.15)]',
  secondary: 'bg-card text-ink border border-strong/70 hover:bg-sunken',
  ghost: 'text-brand hover:bg-brand-soft',
  quiet: 'text-muted hover:bg-sunken hover:text-ink',
  danger: 'bg-danger text-white hover:bg-danger/90 dark:text-paper',
};
export type ButtonSize = 'sm' | 'md' | 'lg';
const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-sm',
  md: 'min-h-touch px-4 text-sm',
  lg: 'min-h-touch-lg px-5 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, block, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center gap-2 rounded-tag font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        SIZE[size],
        block && 'w-full',
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

/** Square icon button; label is required (screen readers + tooltip). */
export const IconButton = forwardRef<HTMLButtonElement, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & { label: string; badge?: number | boolean }>(
  function IconButton({ label, badge, className, children, type, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        aria-label={label}
        title={label}
        className={cx('relative grid h-11 w-11 shrink-0 place-items-center rounded-tag text-muted transition-colors hover:bg-sunken hover:text-ink', className)}
        {...rest}
      >
        {children}
        {badge ? (
          <span className="absolute right-1.5 top-1.5 min-w-[18px] rounded-full bg-danger px-1 text-center text-[11px] font-bold leading-[18px] text-white">
            {typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : ''}
          </span>
        ) : null}
      </button>
    );
  },
);

export function Spinner({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div role="status" className={cx('flex items-center justify-center gap-2 py-10 text-muted', className)}>
      <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/** A surface. Panels are quiet: hairline border, no drop shadow unless lifted. */
export function Panel({ children, className, as: As = 'section', title, action, pad = true }: { children: ReactNode; className?: string; as?: 'section' | 'div' | 'article'; title?: ReactNode; action?: ReactNode; pad?: boolean }) {
  return (
    <As className={cx('rounded-panel border border-line bg-card shadow-panel', className)}>
      {(title || action) && (
        <header className="flex min-h-12 items-center justify-between gap-3 border-b border-line px-4 py-2">
          {title && <h2 className="text-[15px] font-semibold">{title}</h2>}
          {action}
        </header>
      )}
      <div className={pad ? 'p-4' : undefined}>{children}</div>
    </As>
  );
}

export const errorText = (e: unknown): string =>
  e instanceof ApiError ? e.fieldMessages.join(' ') || e.message : e instanceof TypeError ? 'Cannot reach the server. Check the connection and try again.' : e instanceof Error ? e.message : 'Something went wrong. Try again.';

export type Tone = 'neutral' | 'brand' | 'manila' | 'ok' | 'rule' | 'danger' | 'warn' | 'info' | 'violet';
export const TONE_SOFT: Record<Tone, string> = {
  neutral: 'bg-sunken text-muted border border-line',
  brand: 'bg-brand-soft text-brand',
  manila: 'bg-manila-soft text-manila',
  ok: 'bg-ok-soft text-ok',
  rule: 'bg-rule-soft text-rule',
  danger: 'bg-danger-soft text-danger',
  warn: 'bg-warn-soft text-warn',
  info: 'bg-info-soft text-info',
  violet: 'bg-violet-soft text-violet',
};

const NOTE_ICON: Partial<Record<Tone, ReactNode>> = {
  info: <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />,
  ok: <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />,
  brand: <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />,
  warn: <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />,
  danger: <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />,
};

export function Note({ tone = 'info', children, icon, className }: { tone?: Tone; children: ReactNode; icon?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex items-start gap-2 rounded-tag p-3 text-sm', TONE_SOFT[tone], className)}>
      {icon ?? NOTE_ICON[tone]}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const holds = legalHolds(error);
  if (holds) {
    // 423: a record is locked by a rule (kept from the base).
    return (
      <div role="alert" className="rounded-tag bg-warn-soft p-3 text-sm text-warn">
        <p className="flex items-center gap-2 font-semibold">
          <AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
          Under legal hold — it cannot be changed, moved or deleted
        </p>
        {holds.length > 0 && (
          <ul className="mt-1 list-disc pl-8">
            {holds.map((h) => (
              <li key={h.id}>
                {h.name}
                {h.caseRef ? ` (${h.caseRef})` : ''}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1">A Records Officer can release the hold under Records › Legal holds.</p>
      </div>
    );
  }
  return (
    <div role="alert" className="flex items-start gap-2 rounded-tag bg-danger-soft p-3 text-sm text-danger">
      <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="flex-1">{errorText(error)}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="font-semibold underline underline-offset-2">
          Try again
        </button>
      )}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="code rounded border border-line bg-sunken px-1.5 py-0.5 text-[12px] text-muted">{children}</kbd>;
}
